import { heroImage, heroImageAlt, siteInfo } from '@/mocks/site';

const infoRows = [
  { label: '開催日', value: siteInfo.dateLabel },
  { label: '会場', value: siteInfo.venueLabel },
  { label: '入場料', value: siteInfo.admissionLabel },
  { label: '主催', value: siteInfo.organizer },
];

export default function Hero() {
  return (
    <section
      id="top"
      className="relative w-full overflow-hidden bg-foreground-950 md:flex md:min-h-screen md:items-center"
    >
      {/* ---- desktop: full-bleed background ---- */}
      <div className="absolute inset-0 hidden overflow-hidden md:block">
        <div className="animate-hero-drift h-full w-full">
          <img
            src={heroImage}
            alt={heroImageAlt}
            className="animate-hero-zoom h-full w-full object-cover object-bottom"
          />
        </div>
        <div className="animate-hero-veil absolute inset-0 bg-foreground-950/45" />
        <div className="animate-hero-veil absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/35 to-foreground-950/95" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-foreground-950/90 to-transparent" />
      </div>

      <div className="relative mx-auto hidden w-full max-w-4xl flex-col items-center px-10 pb-24 pt-32 pr-20 text-center md:flex">
        <div className="animate-fade-up flex items-center gap-3" style={{ animationDelay: '200ms' }}>
          <span className="h-px w-12 bg-primary-400" />
          <span className="en-label text-[11px] text-background-50/85">{siteInfo.organizer} PRESENTS</span>
          <span className="h-px w-12 bg-primary-400" />
        </div>

        <p
          className="animate-fade-up mt-5 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1"
          style={{ animationDelay: '300ms' }}
        >
          <span className="text-[10.5px] font-bold tracking-[0.2em] text-accent-300">復興イベント</span>
          <span className="font-heading text-[24px] font-black tracking-[0.06em] text-background-50">
            {siteInfo.eventName}
          </span>
          <span className="en-label text-[9.5px] text-background-50/50">{siteInfo.eventNameEn}</span>
        </p>

        <h1
          className="animate-fade-up mt-6 font-heading text-[50px] font-black leading-[1.3] tracking-[-0.01em] text-background-50 lg:text-[58px]"
          style={{ animationDelay: '420ms' }}
        >
          いつもの駐車場が、
          <br />
          いちばん近い祭になる。
        </h1>

        <p
          className="animate-fade-up mt-6 max-w-xl text-[14px] leading-[1.95] text-background-50/80"
          style={{ animationDelay: '650ms' }}
        >
          屋台、キッチンカー、ハンドメイド。熊本県内のトライアル各店舗に集まって、まちに賑わいを戻していく一日です。
        </p>

        <div
          className="animate-fade-up mt-4 inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-foreground-950/30 px-3.5 py-1.5 backdrop-blur-sm"
          style={{ animationDelay: '800ms' }}
        >
          <i className="ri-map-pin-line text-[13px] text-accent-300" aria-hidden="true" />
          <span className="text-[11px] tracking-wider text-background-50/90">
            {siteInfo.area} / トライアル各店舗で継続開催
          </span>
        </div>

        <div
          className="animate-fade-up mt-10 grid w-full max-w-2xl grid-cols-4 gap-x-6 border-t border-background-50/25 pt-7"
          style={{ animationDelay: '950ms' }}
        >
          {infoRows.map((row) => (
            <div key={row.label}>
              <p className="en-label text-[9.5px] text-background-50/55">{row.label}</p>
              <p className="mt-1.5 font-heading text-[15px] font-bold tracking-wide text-background-50">
                {row.value}
              </p>
            </div>
          ))}
        </div>

        <p
          className="animate-fade-up mt-4 text-[10.5px] tracking-wide text-background-50/60"
          style={{ animationDelay: '1000ms' }}
        >
          {siteInfo.venueNote}
        </p>

        <div className="animate-fade-up mt-10 flex flex-col items-center gap-5" style={{ animationDelay: '1150ms' }}>
          <a
            href="#vendor"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-7 py-4 text-[13.5px] font-bold text-foreground-950 transition-colors duration-300 hover:bg-accent-300"
          >
            出店のご応募はこちら
            <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
          </a>

          <div className="flex flex-col items-center gap-2">
            <span className="en-label text-[9px] text-background-50/60">SCROLL</span>
            <span className="relative block h-10 w-px bg-background-50/25">
              <span className="animate-scroll-dot absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-background-50" />
            </span>
          </div>
        </div>
      </div>

      {/* ---- mobile: poster card + text below ---- */}
      <div className="relative flex w-full flex-col md:hidden">
        <div className="relative w-full overflow-hidden bg-foreground-900">
          <img
            src={heroImage}
            alt={heroImageAlt}
            className="animate-hero-zoom aspect-[675/700] w-full object-cover object-bottom"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-foreground-950 via-foreground-950/80 to-transparent" />
        </div>

        <div className="relative -mt-10 px-5 pb-12 pr-14">
          <div className="animate-fade-up flex items-center gap-3" style={{ animationDelay: '200ms' }}>
            <span className="h-px w-8 bg-primary-400" />
            <span className="en-label text-[10px] text-background-50/85">{siteInfo.organizer} PRESENTS</span>
          </div>

          <p
            className="animate-fade-up mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1"
            style={{ animationDelay: '300ms' }}
          >
            <span className="text-[10.5px] font-bold tracking-[0.2em] text-accent-300">復興イベント</span>
            <span className="font-heading text-[19px] font-black tracking-[0.04em] text-background-50">
              {siteInfo.eventName}
            </span>
            <span className="en-label text-[9.5px] text-background-50/50">{siteInfo.eventNameEn}</span>
          </p>

          <h1
            className="animate-fade-up mt-4 font-heading text-[26px] font-black leading-[1.32] tracking-[-0.01em] text-background-50 sm:text-[34px]"
            style={{ animationDelay: '420ms' }}
          >
            いつもの駐車場が、
            <br />
            いちばん近い祭になる。
          </h1>

          <p
            className="animate-fade-up mt-4 text-[12.5px] leading-[1.9] text-background-50/75"
            style={{ animationDelay: '650ms' }}
          >
            屋台、キッチンカー、ハンドメイド。熊本県内のトライアル各店舗に集まって、まちに賑わいを戻していく一日です。
          </p>

          <div
            className="animate-fade-up mt-3 inline-flex items-center gap-2 rounded-full border border-background-50/25 px-3.5 py-1.5"
            style={{ animationDelay: '800ms' }}
          >
            <i className="ri-map-pin-line text-[13px] text-accent-300" aria-hidden="true" />
            <span className="text-[11px] tracking-wider text-background-50/85">
              {siteInfo.area} / トライアル各店舗で継続開催
            </span>
          </div>

          <div
            className="animate-fade-up mt-6 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-background-50/25 pt-5"
            style={{ animationDelay: '950ms' }}
          >
            {infoRows.map((row) => (
              <div key={row.label}>
                <p className="en-label text-[9.5px] text-background-50/55">{row.label}</p>
                <p className="mt-1.5 font-heading text-[13px] font-bold tracking-wide text-background-50">
                  {row.value}
                </p>
              </div>
            ))}
          </div>

          <p
            className="animate-fade-up mt-3 text-[10px] tracking-wide text-background-50/55"
            style={{ animationDelay: '1000ms' }}
          >
            {siteInfo.venueNote}
          </p>

          <div className="animate-fade-up mt-6" style={{ animationDelay: '1150ms' }}>
            <a
              href="#vendor"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-3 text-[12.5px] font-bold text-foreground-950 transition-colors duration-300 hover:bg-accent-300"
            >
              出店のご応募はこちら
              <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}